import React, { ReactNode, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'antd';
import { CaretDownOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Catcher, Loading } from '../../index';
import { NotificationMessage } from './NotificationMessage';
import cx from 'classnames';
import { IServerNotificationAPIGETPayload, IServerNotificationItem } from '../../../interfaces';
import { useUiIsSpinnerFound, useUiServerNotificationsAllCount, useUiServerNotificationsList } from '../../../hooks';
import {
    changeStatusServerNotificationsItemNew,
    getServerNotificationsNextPage,
    setIsPopupOpen,
} from '../../../middleware';
import { ITrServerNotification, tB, tI } from '../../../translate';
import Styles from '../m_styles.less';

type INotificationsList = {
    isShowRead: boolean;
};

export const NotificationsList: React.FC<INotificationsList> = observer(({ isShowRead }: INotificationsList) => {
    const isLoading = useUiIsSpinnerFound(['GET_SERVER_NOTIFICATIONS_NEW', 'PATCH_SERVER_NOTIFICATION_NEW']);
    const list = useUiServerNotificationsList();
    const allCount = useUiServerNotificationsAllCount();

    useEffect(() => {
        setIsPopupOpen(true);
        return (): void => {
            setIsPopupOpen(false);
        };
    }, []);

    if (!list.length) {
        return (
            <Catcher>
                <div className={Styles.notNotifications}>
                    <div className={Styles.notificationContent}>
                        <Loading isLoading={isLoading} />
                        {tI(isShowRead ? 'No notifications' : 'No new notifications')}
                    </div>
                </div>
            </Catcher>
        );
    }

    const getNextReadPage = () => {
        getServerNotificationsNextPage(isShowRead);
    };
    const changeStatus = (id: string, isCurrentRead?: boolean): void => {
        changeStatusServerNotificationsItemNew(id, !isCurrentRead);
    };

    let previousDate = '###';
    const messageList: ReactNode = list?.map((i: IServerNotificationItem, index: number) => {
        const Item = (
            <div key={i.id}>
                <Catcher>
                    {i.dateAgo && previousDate !== i.dateAgo ? i.dateAgo : null}
                    <div
                        className={cx(
                            Styles.notificationItem,
                            { [Styles.notificationEven]: index % 2 === 0 },
                            // { [Styles.notificationEven]: index % 2 === 0 && i.isRead },
                            // { [Styles.notificationRead]: !i.isRead },
                        )}
                    >
                        {!i.isRead ? <div className={Styles.notificationDot} /> : null}
                        <div className={cx(Styles.message, { [Styles.unRead]: !i.isRead })}>
                            <NotificationMessage
                                id={i.id}
                                event={i.event as ITrServerNotification}
                                payload={i.payload as IServerNotificationAPIGETPayload}
                            />
                        </div>
                        <div className={Styles.toolBar}>
                            <CheckCircleOutlined
                                className={cx(Styles.button, { [Styles.blue]: !i.isRead })}
                                onClick={() => changeStatus(i.id, i.isRead)}
                                title={tI(!i.isRead ? 'mark notification as read' : 'mark notification as unread')}
                            />
                        </div>
                    </div>
                </Catcher>
            </div>
        );

        if (i.dateAgo && previousDate !== i.dateAgo) {
            previousDate = i.dateAgo;
        }

        return Item;
    });

    return (
        <Catcher>
            <div key={isShowRead ? 'read' : 'unread'} className={Styles.notifications}>
                <div className={Styles.notificationContent}>
                    <Loading isLoading={isLoading} />
                    {messageList}
                </div>
                {list.length < allCount ? (
                    <Button onClick={getNextReadPage} type="primary" icon={<CaretDownOutlined />}>
                        {tB('More')}
                    </Button>
                ) : null}
            </div>
        </Catcher>
    );
});
